<div class="sc-overlay" *ngIf="open" (click)="closePanel()"></div>

<div
  class="sc-panel"
  *ngIf="open"
  role="dialog"
  aria-modal="true"
  aria-label="Supplier contact details"
>
  <div class="sc-panel__bar"></div>

  <!-- Header -->
  <div class="sc-header">
    <div class="sc-header__icon"><mat-icon>groups</mat-icon></div>
    <div class="sc-header__text">
      <h3 class="sc-header__title">
        Suppliers on this RFQ
        <span class="sc-header__count" *ngIf="!loading">{{ suppliers?.length || 0 }}</span>
      </h3>
      <p class="sc-header__sub">
        <span *ngIf="rfqRefNumber">{{ rfqRefNumber }}</span>
        <span *ngIf="rfqRefNumber && !loading" class="sc-dot">·</span>
        <span *ngIf="!loading">{{ callableCount }} can be called directly</span>
      </p>
    </div>
    <button type="button" class="sc-close" (click)="closePanel()" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- The result of the last call attempt sits above the list. -->
  <div class="sc-notice-slot">
    <app-call-notice [notice]="notice" (dismiss)="dismissNotice()"></app-call-notice>
  </div>

  <!-- Calling switched off entirely: say so once, at the top. -->
  <div class="sc-banner sc-banner--muted" *ngIf="!callingEnabled && !loading">
    <mat-icon>info</mat-icon>
    <span>
      {{ provider }} click-to-call is not enabled for this portal, so numbers below have to be
      dialled manually.
    </span>
  </div>

  <!-- Filter: useful the moment an RFQ goes to more than a handful. -->
  <div class="sc-search" *ngIf="!loading && (suppliers?.length || 0) > 5">
    <mat-icon>search</mat-icon>
    <input
      type="text"
      [(ngModel)]="search"
      placeholder="Filter by company, contact, email or number"
      aria-label="Filter suppliers"
    />
    <button type="button" *ngIf="search" (click)="search = ''" aria-label="Clear filter">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Body -->
  <div class="sc-body">
    <div class="sc-loading" *ngIf="loading">
      <span class="sc-spinner"></span>
      <span>Looking up the supplier contacts…</span>
    </div>

    <div class="sc-empty" *ngIf="!loading && error">
      <mat-icon>error_outline</mat-icon>
      <div>
        <div class="sc-empty__title">Contacts could not be loaded</div>
        <div class="sc-empty__detail">{{ error }}</div>
      </div>
    </div>

    <div class="sc-empty" *ngIf="!loading && !error && !(suppliers?.length || 0)">
      <mat-icon>person_off</mat-icon>
      <div>
        <div class="sc-empty__title">No suppliers on this RFQ yet</div>
        <div class="sc-empty__detail">
          Once the RFQ is submitted to suppliers, everyone it went to appears here with their
          contact details.
        </div>
      </div>
    </div>

    <div class="sc-empty" *ngIf="!loading && !error && (suppliers?.length || 0) && !visibleSuppliers.length">
      <mat-icon>search_off</mat-icon>
      <div>
        <div class="sc-empty__title">Nothing matches “{{ search }}”</div>
        <div class="sc-empty__detail">Clear the filter to see all {{ suppliers.length }} suppliers.</div>
      </div>
    </div>

    <!-- One card per supplier. Staggered entrance so a long list reads as a
         list arriving rather than a block appearing. -->
    <div
      class="sc-card"
      *ngFor="let s of visibleSuppliers; let i = index; trackBy: trackBySupplier"
      [class.sc-card--active]="activeSupplierId === s.supplierId"
      [style.animation-delay]="(i < 12 ? i * 40 : 0) + 'ms'"
    >
      <div class="sc-card__avatar">{{ initials(s) }}</div>

      <div class="sc-card__main">
        <div class="sc-card__name">
          {{ s.companyName }}
          <span class="sc-chip sc-chip--quoted" *ngIf="s.hasQuoted">
            <mat-icon>check_circle</mat-icon>Quoted
          </span>
          <span class="sc-chip sc-chip--waiting" *ngIf="!s.hasQuoted && s.viewed">
            <mat-icon>visibility</mat-icon>Viewed
          </span>
          <span class="sc-chip sc-chip--pending" *ngIf="!s.hasQuoted && !s.viewed">
            <mat-icon>schedule</mat-icon>Awaiting response
          </span>
        </div>

        <div class="sc-card__meta">
          <span *ngIf="s.contactPerson">
            <mat-icon>person</mat-icon>{{ s.contactPerson }}<ng-container *ngIf="s.jobTitle">, {{ s.jobTitle }}</ng-container>
          </span>
          <span *ngIf="s.vendorCode"><mat-icon>badge</mat-icon>{{ s.vendorCode }}</span>
          <span *ngIf="s.quoteRefNumber"><mat-icon>request_quote</mat-icon>{{ s.quoteRefNumber }}</span>
        </div>

        <div class="sc-card__links">
          <a *ngIf="s.email" [href]="'mailto:' + s.email" class="sc-link">
            <mat-icon>mail</mat-icon>{{ s.email }}
          </a>
          <span *ngIf="s.phone" class="sc-link sc-link--plain">
            <mat-icon>call</mat-icon>{{ s.phone }}
          </span>
          <span *ngIf="s.alternatePhone" class="sc-link sc-link--plain">
            <mat-icon>smartphone</mat-icon>{{ s.alternatePhone }}
          </span>
          <span *ngIf="s.fax" class="sc-link sc-link--plain">
            <mat-icon>print</mat-icon>{{ s.fax }}
          </span>
        </div>
      </div>

      <div class="sc-card__actions">
        <button
          type="button"
          class="sc-call"
          [class.sc-call--muted]="!s.callable"
          (click)="callSupplier(s)"
          [matTooltip]="
            s.callable
              ? 'Call ' + s.companyName + ' using ' + provider
              : 'No phone number on record'
          "
          matTooltipPosition="left"
        >
          <mat-icon>{{ s.callable ? 'call' : 'phone_disabled' }}</mat-icon>
          <span>Call</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Footer: the 3CX prerequisite, stated once where it belongs. -->
  <div class="sc-footer" *ngIf="!loading && callingEnabled && (suppliers?.length || 0)">
    <mat-icon>headset_mic</mat-icon>
    <span>
      Calls are placed through {{ provider }} on this computer. Make sure {{ provider }} is running
      and set as the handler for telephone links before you call.
    </span>
  </div>
</div>
